import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMenu, FiX, FiGlobe, FiMoon, FiSun, FiUser, FiBell } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import CurrencySelector from './CurrencySelector';
import LanguageSelector from './LanguageSelector';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md shadow-lg">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-amber-500 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">🌾</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold gradient-text">DAN</h1>
              <p className="text-xs text-emerald-600">Decentralized Agribusiness Network</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="nav-link">Dashboard</Link>
                <Link to="/marketplace" className="nav-link">Marketplace</Link>
                <Link to="/education-hub" className="nav-link">Education Hub</Link>
                <Link to="/orders" className="nav-link">Orders</Link>
                {user?.role === 'admin' && (
                  <Link to="/admin" className="nav-link text-red-600">Admin Panel</Link>
                )}
              </>
            ) : (
              <>
                <Link to="/" className="nav-link">Home</Link>
                <Link to="/education-hub" className="nav-link">Education Hub</Link>
                <a href="#features" className="nav-link">Features</a>
                <a href="#how-it-works" className="nav-link">How it Works</a>
              </>
            )}
          </div>

          {/* Controls */}
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSelector />
            <CurrencySelector />
            
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
            >
              {theme === 'dark' ? <FiSun size={20} /> : <FiMoon size={20} />}
            </button>

            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <button className="relative p-2">
                  <FiBell size={22} className="text-emerald-700" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                </button>
                <div className="relative group">
                  <button className="flex items-center space-x-2 p-2 rounded-lg bg-emerald-50 hover:bg-emerald-100">
                    <FiUser size={20} className="text-emerald-700" />
                    <span className="font-medium text-emerald-800">{user?.name?.split(' ')[0]}</span>
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-2xl border border-emerald-100 hidden group-hover:block">
                    <div className="p-4 border-b">
                      <p className="font-semibold text-emerald-800">{user?.name}</p>
                      <p className="text-sm text-emerald-600">{user?.email}</p>
                      <span className={`inline-block mt-1 px-2 py-1 rounded-full text-xs ${user?.kycVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {user?.kycVerified ? 'KYC Verified' : 'KYC Pending'}
                      </span>
                    </div>
                    <Link to="/dashboard" className="block px-4 py-3 hover:bg-emerald-50 text-emerald-700">Dashboard</Link>
                    <Link to="/profile" className="block px-4 py-3 hover:bg-emerald-50 text-emerald-700">Profile</Link>
                    <button onClick={handleLogout} className="block w-full text-left px-4 py-3 hover:bg-red-50 text-red-600 border-t">
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/login" className="px-6 py-2 rounded-xl text-emerald-700 font-medium hover:bg-emerald-50 transition-colors">
                  Login
                </Link>
                <Link to="/signup" className="btn-primary">Sign Up</Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg bg-emerald-100 text-emerald-700"
          >
            {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-emerald-100 animate-slide-up">
            <div className="space-y-3">
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className="block py-2 px-4 rounded-lg hover:bg-emerald-50">Dashboard</Link>
                  <Link to="/marketplace" className="block py-2 px-4 rounded-lg hover:bg-emerald-50">Marketplace</Link>
                  <Link to="/education-hub" className="block py-2 px-4 rounded-lg hover:bg-emerald-50">Education Hub</Link>
                  <Link to="/orders" className="block py-2 px-4 rounded-lg hover:bg-emerald-50">Orders</Link>
                  {user?.role === 'admin' && (
                    <Link to="/admin" className="block py-2 px-4 rounded-lg hover:bg-red-50 text-red-600">Admin Panel</Link>
                  )}
                </>
              ) : (
                <>
                  <Link to="/" className="block py-2 px-4 rounded-lg hover:bg-emerald-50">Home</Link>
                  <Link to="/education-hub" className="block py-2 px-4 rounded-lg hover:bg-emerald-50">Education Hub</Link>
                  <a href="#features" className="block py-2 px-4 rounded-lg hover:bg-emerald-50">Features</a>
                  <a href="#how-it-works" className="block py-2 px-4 rounded-lg hover:bg-emerald-50">How it Works</a>
                </>
              )}
              
              <div className="pt-4 border-t space-y-3">
                <LanguageSelector />
                <CurrencySelector />
                <button onClick={toggleTheme} className="w-full flex items-center justify-between py-2 px-4 rounded-lg hover:bg-emerald-50">
                  <span>Theme</span>
                  {theme === 'dark' ? <FiSun /> : <FiMoon />}
                </button>
              </div>
              
              {isAuthenticated ? (
                <button onClick={handleLogout} className="w-full py-2 px-4 rounded-lg bg-red-50 text-red-600 font-medium">
                  Logout
                </button>
              ) : (
                <div className="space-y-2">
                  <Link to="/login" className="block py-2 px-4 rounded-lg text-center border border-emerald-200 text-emerald-700">
                    Login
                  </Link>
                  <Link to="/signup" className="block py-2 px-4 rounded-lg text-center btn-primary">
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;